import { useState, useEffect } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import schedulingService from '../../../services/scheduling.service';
import { TerminalSettings } from '../../../types/scheduling';
import { useToast } from '../../../contexts/ToastContext';
import Toggle from '../../../components/Toggle';

interface TerminalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function TerminalSettingsModal({ isOpen, onClose }: TerminalSettingsModalProps) {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<TerminalSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      schedulingService.getTerminalSettings()
        .then(setSettings)
        .catch((err) => {
          showToast({ type: 'error', message: 'Failed to load terminal settings' });
          console.error(err);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, showToast]);

  const handleUpdate = async (
    terminal: TerminalSettings,
    field: keyof TerminalSettings,
    value: string | number | boolean
  ) => {
    setSaving(terminal.id);
    try {
      const updated = await schedulingService.updateTerminalSettings(terminal.id, {
        [field]: value,
      });
      setSettings((prev) =>
        prev.map((s) => (s.id === terminal.id ? updated : s))
      );
    } catch (err) {
      showToast({ type: 'error', message: 'Failed to update setting' });
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  // Alt+C closes. Each terminal row saves on its own toggle, so there's no
  // single Save to bind here.
  useAltHotkeys({ C: saving != null ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        aria-labelledby="terminal-settings-modal-title"
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel
                ref={panelRef}
                style={panelStyle}
                className="w-full max-w-4xl overflow-hidden rounded-xl bg-panel shadow-xl"
              >
                <ModalHeader
                  title="Terminal Settings"
                  titleId="terminal-settings-modal-title"
                  onClose={onClose}
                  dragHandleProps={dragHandleProps}
                />

                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading...</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-muted border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="text-left py-2 px-2 font-medium">Terminal</th>
                          <th className="text-left py-2 px-2 font-medium">Truck Pool Type</th>
                          <th className="text-left py-2 px-2 font-medium">Local Deliveries Per Day</th>
                          <th className="text-left py-2 px-2 font-medium">Prepulls Per Day</th>
                          <th className="text-left py-2 px-2 font-medium">Accepting New Lanes</th>
                          <th className="text-left py-2 px-2 font-medium">Prepulls</th>
                        </tr>
                      </thead>
                      <tbody className="text-label">
                        {settings.map((terminal) => (
                          <tr
                            key={terminal.id}
                            className={`border-b border-gray-100 dark:border-gray-700 ${
                              saving === terminal.id ? 'opacity-50' : ''
                            }`}
                          >
                            <td className="py-2 px-2 font-medium text-heading">
                              {terminal.terminal}
                            </td>
                            <td className="py-2 px-2">
                              <select
                                value={terminal.truck_pool_type}
                                onChange={(e) =>
                                  handleUpdate(terminal, 'truck_pool_type', e.target.value)
                                }
                                disabled={saving === terminal.id}
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
                              >
                                <option value="Separate">Separate</option>
                                <option value="Combined">Combined</option>
                              </select>
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                step="0.25"
                                value={terminal.local_deliveries_per_day}
                                onChange={(e) =>
                                  handleUpdate(
                                    terminal,
                                    'local_deliveries_per_day',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                disabled={saving === terminal.id}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <input
                                type="number"
                                step="0.5"
                                value={terminal.prepulls_per_day}
                                onChange={(e) =>
                                  handleUpdate(
                                    terminal,
                                    'prepulls_per_day',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                disabled={saving === terminal.id}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
                              />
                            </td>
                            <td className="py-2 px-2">
                              <select
                                value={terminal.accepting_new_lanes ? 'Yes' : 'No'}
                                onChange={(e) =>
                                  handleUpdate(
                                    terminal,
                                    'accepting_new_lanes',
                                    e.target.value === 'Yes'
                                  )
                                }
                                disabled={saving === terminal.id}
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
                              >
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            </td>
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs ${terminal.prepull_type === 'Local' ? 'font-semibold text-heading' : 'text-gray-400 dark:text-gray-500'}`}>Local</span>
                                <Toggle
                                  checked={terminal.prepull_type === 'OTR'}
                                  onChange={(checked) =>
                                    handleUpdate(terminal, 'prepull_type', checked ? 'OTR' : 'Local')
                                  }
                                  disabled={saving === terminal.id}
                                  size="sm"
                                  ariaLabel={`Prepull type for ${terminal.terminal}`}
                                />
                                <span className={`text-xs ${terminal.prepull_type === 'OTR' ? 'font-semibold text-heading' : 'text-gray-400 dark:text-gray-500'}`}>OTR</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
